import React from 'react';
import { Users, User, Calculator, Users as Family, Clock, TrendingUp, History, Star } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент панели быстрых действий
 */
const QuickActionsBar = ({ 
  formData, 
  setFormData,
  activeTab,
  setActiveTab
}) => {
  // Предустановленные шаблоны бюджетов
  const budgetTemplates = [
    { 
      id: 'personal', 
      name: 'Личный бюджет', 
      icon: <User className="w-4 h-4" />,
      config: {
        budgetType: 'personal',
        familyMembers: 1,
        hasChildren: false,
        childrenCount: 0
      }
    },
    { 
      id: 'family', 
      name: 'Семейный бюджет', 
      icon: <Family className="w-4 h-4" />,
      config: {
        budgetType: 'family',
        familyMembers: 2,
        hasChildren: false,
        childrenCount: 0
      }
    },
    { 
      id: 'family-with-children', 
      name: 'Семья с детьми', 
      icon: <Calculator className="w-4 h-4" />,
      config: {
        budgetType: 'family',
        familyMembers: 2,
        hasChildren: true,
        childrenCount: 1
      }
    },
    { 
      id: 'pensioner', 
      name: 'Пенсионный бюджет', 
      icon: <Clock className="w-4 h-4" />,
      config: {
        budgetType: 'personal',
        familyMembers: 1,
        hasChildren: false,
        childrenCount: 0,
        incomes: [
          { id: '1', name: 'Пенсия', amount: '20000', category: 'pension', recurring: true, frequency: 'monthly' }
        ]
      }
    },
    { 
      id: 'savings', 
      name: 'Бюджет накоплений', 
      icon: <Star className="w-4 h-4" />,
      config: {
        distribution: {
          savingsPercentage: 20,
          entertainmentPercentage: 5,
          essentialsPercentage: 70,
          investmentsPercentage: 5
        }
      }
    },
    { 
      id: 'investments', 
      name: 'Инвестиционный бюджет', 
      icon: <TrendingUp className="w-4 h-4" />,
      config: {
        investments: {
          enabled: true,
          monthlyContribution: '10000',
          portfolioValue: '100000',
          expectedReturn: '7'
        },
        distribution: {
          savingsPercentage: 5,
          entertainmentPercentage: 5,
          essentialsPercentage: 70,
          investmentsPercentage: 20
        }
      }
    }
  ];

  // Функция применения шаблона
  const applyTemplate = (templateId) => {
    const template = budgetTemplates.find(t => t.id === templateId);
    if (!template) return;

    setFormData(prev => {
      // Создаем новый объект бюджета, комбинируя текущие данные с шаблоном
      let newState = { ...prev };
      
      // Обрабатываем простые поля
      Object.entries(template.config).forEach(([key, value]) => {
        if (typeof value !== 'object') {
          newState[key] = value;
        }
      });
      
      // Обрабатываем вложенные объекты
      ['distribution', 'investments', 'settings'].forEach(section => {
        if (template.config[section]) {
          newState[section] = { ...newState[section], ...template.config[section] };
        }
      });
      
      // Обрабатываем массивы, если они определены в шаблоне
      ['incomes', 'expenses'].forEach(arrayName => {
        if (template.config[arrayName]) {
          newState[arrayName] = [...template.config[arrayName]];
        }
      });
      
      // Обрабатываем сложные вложенные структуры
      if (template.config.savings) {
        newState.savings = { ...newState.savings, ...template.config.savings };
        
        // Дополнительно обрабатываем подструктуры
        ['emergencyFund', 'retirementFund'].forEach(fund => {
          if (template.config.savings?.[fund]) {
            newState.savings[fund] = { ...newState.savings[fund], ...template.config.savings[fund] };
          }
        });
        
        // Обрабатываем массивы в подструктурах
        if (template.config.savings?.goals) {
          newState.savings.goals = [...template.config.savings.goals];
        }
      }
      
      return newState;
    });
  };

  return (
    <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-medium text-gray-700 dark:text-gray-300">
          Быстрые действия
        </h3>
        <button 
          onClick={() => setActiveTab(activeTab === 'overview' ? 'income' : 'overview')}
          className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
        >
          {activeTab === 'overview' ? 'Перейти к доходам' : 'Общий обзор'}
        </button>
      </div>

      {/* Шаблоны бюджетов */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
        {budgetTemplates.map(template => (
          <button
            key={template.id}
            className="p-2 text-xs text-center rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/70 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            onClick={() => applyTemplate(template.id)}
            title={`Применить шаблон "${template.name}"`}
          >
            <div className="flex justify-center mb-1">
              {template.icon}
            </div>
            <div className="font-medium text-gray-800 dark:text-gray-200 truncate">
              {template.name}
            </div>
          </button>
        ))}
      </div>

      {/* Переключение между разделами */}
      <div className="mt-4 grid grid-cols-3 gap-1">
        <Button
          size="sm"
          variant={activeTab === 'income' ? 'primary' : 'outline'}
          onClick={() => setActiveTab('income')}
        >
          Доходы
        </Button>
        <Button
          size="sm"
          variant={activeTab === 'expenses' ? 'primary' : 'outline'}
          onClick={() => setActiveTab('expenses')}
        >
          Расходы
        </Button>
        <Button
          size="sm"
          variant={activeTab === 'savings' ? 'primary' : 'outline'}
          onClick={() => setActiveTab('savings')}
        >
          Цели
        </Button>
      </div>
    </div>
  );
};

export default QuickActionsBar;